import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import syncDiverterService from '../services/syncDiverterService';
import Toggle from './shared/Toggle';
import ConfirmModal from './shared/ConfirmModal';
import './OxiDequeueAdmin.css';
import './AdminSyncConnectQueries.css';

// Admin › Infra › Sync Diverter.
//
// Some sites send OXI to the legacy stack (ire-api.hconnect.io) and cannot be repointed. An API key listed here is
// diverted: v1-hc-oxi-enqueue passes the site's messages to the hub, and v1-hc-oxi-dequeue its polls, so the site runs
// on its hub sync project without being touched. The list lives on the LEGACY database (sync_diverter) because the
// legacy lambdas read it on every request — a key added or switched off here applies to the site's next call.
//
// A key is accepted only when a hub OPERAOXI connector carries it (otherwise the hub would refuse the site), and each
// row shows the hub sync project its messages land in and the legacy connector it was set up on. Every change is on
// the audit trail; revealing a key is a READ.

const when = (v) => (v ? new Date(v).toLocaleString() : '—');
const errorOf = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message;

export default function AdminSyncDiverter() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [checkedAt, setCheckedAt] = useState(null);
    const [keys, setKeys] = useState({});              // id -> revealed key
    const [busy, setBusy] = useState({});              // id -> true while a write is in flight
    const [form, setForm] = useState({ apiKey: '', note: '' });
    const [adding, setAdding] = useState(false);
    const [removing, setRemoving] = useState(null);
    const [noteEdit, setNoteEdit] = useState(null);    // { id, note }

    const load = useCallback(async () => {
        setError(null);
        try {
            const res = (await syncDiverterService.list()).data;
            setRows(res.rows || []);
            setCheckedAt(res.checkedAt);
        } catch (e) {
            setError(errorOf(e));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const withBusy = async (id, fn) => {
        setBusy(b => ({ ...b, [id]: true }));
        setError(null);
        try { await fn(); await load(); } catch (e) { setError(errorOf(e)); }
        finally { setBusy(b => { const n = { ...b }; delete n[id]; return n; }); }
    };

    const add = async (e) => {
        e.preventDefault();
        setAdding(true);
        setError(null);
        try {
            await syncDiverterService.create({ apiKey: form.apiKey.trim(), note: form.note.trim() });
            setForm({ apiKey: '', note: '' });
            await load();
        } catch (err) {
            setError(errorOf(err));
        } finally {
            setAdding(false);
        }
    };

    const reveal = async (id) => {
        try {
            const res = (await syncDiverterService.revealKey(id)).data;
            setKeys(k => ({ ...k, [id]: res.apiKey }));
        } catch (e) {
            setError(errorOf(e));
        }
    };

    const diverting = rows.filter(r => r.active === 'Y').length;

    return (
        <div className="acp-page">
            <div className="acp-header">
                <div>
                    <h1 className="acp-title">Sync Diverter</h1>
                    <p className="acp-subtitle">
                        API keys of sites that send OXI to the legacy stack (ire-api.hconnect.io) and cannot be repointed.
                        A key that is on here and switched on is passed to the hub — messages and polls both — and the site
                        runs on the hub sync project that carries the same key. Changes apply to the site's next call.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button className="acp-btn acp-btn-secondary" onClick={load}>Refresh</button>
                </div>
            </div>

            {error && <div className="os-msg os-msg-error" onClick={() => setError(null)}>{error}</div>}

            <div className="acp-card" style={{ marginBottom: '1rem' }}>
                <div className="acp-card-header"><span className="acp-card-title">Divert a key</span></div>
                <form onSubmit={add} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', padding: '1rem 1.5rem' }}>
                    <input className="odq-filter-select scq-mono" style={{ minWidth: '20rem', flex: 1 }} required
                           placeholder="API key — the hub sync project's Configure Source › API Key" autoComplete="off"
                           value={form.apiKey} onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))} />
                    <input className="odq-filter-select" style={{ minWidth: '16rem', flex: 1 }} maxLength={255}
                           placeholder="Note (optional) — e.g. which site and why"
                           value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                    <button type="submit" className="acp-btn acp-btn-primary" disabled={adding || !form.apiKey.trim()}>
                        {adding ? 'Adding…' : 'Divert to hub'}
                    </button>
                </form>
                <p className="scq-meta" style={{ padding: '0 1.5rem 1rem', margin: 0 }}>
                    The key must already be the API key of a sync project on the hub — a key the hub does not know is refused,
                    because diverting it would stop the site's interface.
                </p>
            </div>

            <div className="acp-card">
                <div className="acp-card-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span className="acp-card-title">{rows.length} key{rows.length === 1 ? '' : 's'} · {diverting} diverting</span>
                    {checkedAt && <span className="scq-meta" style={{ marginLeft: 'auto' }}>Updated {new Date(checkedAt).toLocaleTimeString()}</span>}
                </div>
                {loading && !rows.length ? <div className="acp-empty">Loading…</div>
                    : rows.length === 0 ? <div className="acp-empty">No keys are diverted — every legacy site is handled by the legacy stack.</div>
                    : (
                    <table className="acp-table">
                        <thead>
                            <tr>
                                <th>Diverting</th><th>API key</th><th>Hub sync project</th><th>Legacy connector</th>
                                <th>Forwarded</th><th>Note</th><th>Changed</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id}>
                                    <td>
                                        <Toggle size="sm" on={r.active === 'Y'} onLabel="On" offLabel="Off" busy={!!busy[r.id]}
                                                title={r.active === 'Y' ? 'Stop diverting — the legacy stack handles the site again' : 'Divert to the hub'}
                                                onChange={(on) => withBusy(r.id, () => syncDiverterService.update(r.id, { active: on ? 'Y' : 'N' }))} />
                                    </td>
                                    <td className="scq-mono" style={{ whiteSpace: 'nowrap' }}>
                                        {keys[r.id] != null
                                            ? keys[r.id]
                                            : <>…{r.api_key_hint || '????'} <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => reveal(r.id)}>Show</button></>}
                                    </td>
                                    <td>
                                        {(r.hub || []).length === 0
                                            ? <span className="scq-status scq-status-error" title="No hub OPERAOXI connector has this key — the hub will refuse the site">not on the hub</span>
                                            : r.hub.map(h => (
                                                <div key={h.connectorId}>
                                                    {h.syncId ? <Link to={`/sync/${h.syncId}`}>{h.syncId} · {h.projectName}</Link> : 'no sync project'}
                                                    <div className="scq-meta">connector {h.connectorId}{h.projectStatus ? ` · ${h.projectStatus}` : ''}</div>
                                                </div>
                                            ))}
                                    </td>
                                    <td>
                                        {(r.legacy || []).length === 0 ? <span className="scq-meta">none with this key</span>
                                            : r.legacy.map(l => (
                                                <div key={l.connectorId}>{l.connectorId} · {l.connectorName || '?'}
                                                    <div className="scq-meta">{[l.accountName, l.connectorStatus].filter(Boolean).join(' · ')}</div>
                                                </div>
                                            ))}
                                    </td>
                                    <td>{r.calls ? Number(r.calls).toLocaleString() : '—'}<div className="scq-meta">{r.lastForwarded ? when(r.lastForwarded) : 'not yet'}</div></td>
                                    <td style={{ maxWidth: '18rem' }}>
                                        {noteEdit && noteEdit.id === r.id ? (
                                            <form onSubmit={(e) => { e.preventDefault(); const n = noteEdit.note; setNoteEdit(null); withBusy(r.id, () => syncDiverterService.update(r.id, { note: n })); }}
                                                  style={{ display: 'flex', gap: '0.35rem' }}>
                                                <input className="odq-filter-select" autoFocus maxLength={255} value={noteEdit.note}
                                                       onChange={e => setNoteEdit({ id: r.id, note: e.target.value })} />
                                                <button type="submit" className="acp-btn acp-btn-primary acp-btn-sm">Save</button>
                                                <button type="button" className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setNoteEdit(null)}>Cancel</button>
                                            </form>
                                        ) : (
                                            <>{r.note || <span className="scq-meta">—</span>}{' '}
                                                <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setNoteEdit({ id: r.id, note: r.note || '' })}>Edit</button></>
                                        )}
                                    </td>
                                    <td>{when(r.updated_at)}<div className="scq-meta">{r.updated_by || r.created_by || ''}</div></td>
                                    <td><button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={!!busy[r.id]} onClick={() => setRemoving(r)}>Remove</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {removing && (
                <ConfirmModal
                    title="Stop diverting this key?"
                    message={`Key …${removing.api_key_hint || '????'} is removed from the diverter. The site's next call is handled by the legacy stack again${(removing.legacy || []).length ? '' : ' — and no legacy connector carries this key, so the legacy stack will refuse it'}.`}
                    confirmLabel="Remove"
                    danger
                    onCancel={() => setRemoving(null)}
                    onConfirm={() => { const r = removing; setRemoving(null); withBusy(r.id, () => syncDiverterService.remove(r.id)); }}
                />
            )}
        </div>
    );
}
